import clsx from "clsx";
import React, { useRef, useState, useEffect } from "react";
import moment from "moment";
import { useReactToPrint } from "react-to-print";
import { ITab, ITabs } from "../../types/customizer";
import MyGallery from "../MyGallery";
import "./index.scss";
import PrintSummary from "../PrintSummary";
import {
  REQUIRED_TAB_FIELDS,
  TABS_TO_AVOID,
  getRowContent,
} from "../../../utils/summary";
import { useRingCustomizerContext } from "../../context/RingCustomizerContext";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AnyObject, APP_STYLES, DEFAULT_STYLES } from "../../../utils/basic";
import If from "../If";

declare global {
  interface Window {
    gladlyConfig?: { appId?: string; [k: string]: any };
  }
}

interface SummaryProps {
  images?: string[];
  tabs: ITabs;
  isPDP?: boolean;
}

export const formatSummaryDate = (date: string) => {
  const momentDate = moment(date);
  const formatted = momentDate.format('ddd. MMM. Do*');

  if (momentDate.isSame(moment(), 'day')) {
    return `Today (${formatted})`;
  } else if (momentDate.isSame(moment().add(1, 'day'), 'day')) {
    return `Tomorrow (${formatted})`;
  }
  return formatted; // Default format
};

export const getLastestShippingDate = (shippingOptions: AnyObject) => {
  const dates = Object.values(shippingOptions || {}).filter(date => moment(date, 'YYYY-MM-DD', true).isValid());
  const earliestDate = moment.min(dates.map(date => moment(date)));
  return earliestDate.format('ddd. MMM. Do*')
}

export const getLastestShippingDateHenne = (shippingOptions: AnyObject) => {
  const dates = Object.values(shippingOptions || {}).filter(date => moment(date, 'YYYY-MM-DD', true).isValid());
  const earliestDate = moment.min(dates.map(date => moment(date)));
  return earliestDate.format('ddd, MMM Do, YYYY*')
}

const Summary = ({ images = [], tabs, isPDP = false }: SummaryProps) => {
  const { product, subpath, params, isQueryHandling } = useRingCustomizerContext();
  const [value, setValue] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const componentRef = useRef<HTMLDivElement | null>(null);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: "Summary",
    bodyClass: "print-window",
  });

  const containerClass = clsx({
    "tw-overflow-y-auto tw-overflow-x-hidden tw-h-full swatch-filter-border tw-py-10 tw-px-5":
      !isPDP,
    "tw-w-full tw-h-full tw-py-1 tw-pb-4 tw-bg-blue-50 tw-text-xs summary-pdp":
      isPDP,
  });

  const innerContainerClass = clsx("tw-text-prussian-blue", {
    "tw-w-full lg:tw-w-2/3 xl:tw-pr-[15%] 2xl:tw-pr-[20%]": !isPDP,
    "tw-w-full": isPDP,
  });

  const handleSearch = async() => {
    try {
      const response  = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/rings/searchMagicNumber?magicNumber=${value}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      }).then(res => res.ok ? res.json() : Promise.reject(res));
      const customizeUrl = response.ring_data?.customize_url;
      const bestseller_id = response.ring_data?.bestseller_id;
      if(bestseller_id) {
          // When there is a bestseller id, we append to current URL
          window.location.href += `&bestSeller=${bestseller_id}`;
      }else if(customizeUrl) {
          navigateToUrl(customizeUrl);
          setValue('');
      } else {
          toast.error(response.message)
      }
      console.log(response);
    } catch(error) {
      console.log(error)
    }
  }

  const navigateToUrl = (URL: string, replace = true) => {
    const { pathprefix, subpath} = params.pathParams;
    let navigateUrl = URL;
    if(pathprefix && subpath) {
      navigateUrl = `/${pathprefix}/${subpath}${URL}`;
    } else if(subpath){
      navigateUrl = `/${subpath}${URL}`;
    }

    if(isQueryHandling) {
      navigateUrl = `${location.pathname}?ring_url=${encodeURIComponent(navigateUrl)}`;
    }

    const otherQueryString = new URLSearchParams(params.otherQueryParams).toString();
    if(otherQueryString) {
      navigateUrl += `&${otherQueryString}`;
    }
    navigate(navigateUrl, { replace: replace }); // After URL is navigated it calls a useffect of location dependencies
  }

  const REQUIRED_TABS = tabs.filter((tab: ITab) => REQUIRED_TAB_FIELDS.includes(tab.eventKey) && (!['revolution', 'stonebrook-jewelry', 'foundry'].includes(subpath) || tab.eventKey != 'ringSize'));
  const ADDITIONAL_TABS = tabs.filter((tab: ITab) => !REQUIRED_TAB_FIELDS.includes(tab.eventKey) && !TABS_TO_AVOID.includes(tab.eventKey));
  const styles = APP_STYLES[subpath] || DEFAULT_STYLES;

  useEffect(() => {
    // Only load for these subpaths
    const shouldLoad = ['daniels-jewelers','daniels-jewelers-dev'].includes(subpath);
    if (!shouldLoad) return;

    const src = "https://static.klaviyo.com/onsite/js/QVhRw2/klaviyo.js?company_id=QVhRw2";

    // Prevent duplicates (StrictMode mounts twice in dev)
    if (document.querySelector(`head script[src="${src}"]`)) return;

    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.crossOrigin = "anonymous";
    script.setAttribute("data-loaded-by", "Summary");
    document.head.appendChild(script);

    // Optional cleanup: remove when leaving this page
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [subpath]);

  useEffect(() => {
    const shouldLoad = ['legacy-touch', 'legacy-touch-dev'].includes(subpath);
    if (!shouldLoad || typeof window === 'undefined' || typeof document === 'undefined') return;

    // 1) Provide config BEFORE loading the widget
    window.gladlyConfig = {
      ...(window.gladlyConfig || {}),
      appId: 'tributetech.com-LEGACYTOUCH.COM',
    };

    const src = "https://cdn.gladly.com/chat-sdk/widget.js?q=1759954900670";

    // Prevent duplicates (StrictMode mounts twice in dev)
    if (document.querySelector(`head script[src="${src}"]`)) return;

    // 2) Inject the widget
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.setAttribute('data-gladly-src', src);
    document.head.appendChild(script);

  }, [subpath]);

  return (
    <div className={containerClass}>
      <div
        className={clsx("tw-flex tw-flex-wrap summary", {
          "lg:tw-w-11/12 tw-ml-auto": !isPDP,
        })}
      >
        <div className={innerContainerClass}>
          <If condition={['legacy-touch', 'legacy-touch-dev'].includes(subpath) && styles.showShippingEstimation}>
            <div className='tw-mb-2 tw-font-bold'>Usually ships within 2 weeks</div>
          </If>
          {!isPDP && ['daniels-jewelers', 'daniels-jewelers-dev'].includes(subpath) && (<>
            <button id="print-btn" className="tw-mb-5 !tw-bg-brand-color !tw-text-white hover:tw-text-white tw-px-9 tw-py-2 tw-font-medium tw-normal-case secondary-font-family focus:tw-outline-none" onClick={handlePrint}>Print Summary</button>
            <PrintSummary tabs={tabs} image={images?.[0]} product={product} ref={componentRef} />
          </>)}
          {!isPDP && <p className={clsx("product_description", subpath)}>{product?.description}</p>}
          <If condition={subpath == 'benari-jewelers'}>
            <div className='tw-mt-2' style={{fontSize: '11px'}}>
              <p className="tw-mb-2">This ring includes a free at-home sizing kit that will arrive in 1-3 business days. You will be able to finalize your ring size after receiving the kit.</p>
              <p className="tw-mb-2">Free Standard Delivery: estimated delivery 4-6 weeks</p>
              <p className="tw-mb-2">Don't see your size available? You can special order a size. Contact us.</p>
              <p className="tw-mb-2">Please note, all custom jewelry sales are final.</p>
            </div>
          </If>
          <If condition={['daniels-jewelers','daniels-jewelers-dev'].includes(subpath)}>
            <div className="tw-mt-3 tw-text-lg tw-font-bold">Order Info</div>
          </If>
					<If condition={!['daniels-jewelers','daniels-jewelers-dev', 'legacy-touch', 'legacy-touch-dev'].includes(subpath)}>
            <div className="tw-mt-3 tw-text-lg tw-font-bold">Required</div>
            <div className='tw-text-xs tw-mb-3'>*Magic number is the only thing required for an order: now includes Engraving and Ring size</div>
          </If>
          <div className="summary-container tw-mt-3 ml-3">
            <div className="tw-mt-1">
              <If condition={subpath == 'rogers-jewelry'}>
                <p style={{fontWeight: "500"}}><span style={{fontWeight: "700"}}>Magic Number</span><br/><span style={{fontSize: "12px"}}>(Please provide us with this magic number for your design)</span></p>
              </If>
              <If condition={subpath != 'rogers-jewelry'}>
                <p>Magic Number</p>
              </If>
              <p>{product?.magic_number}</p>
            </div>
            {REQUIRED_TABS.map((tab: ITab, index) => (
              <React.Fragment key={index}>
                {" "}
                {getRowContent(tab, index, { isPDP })}
              </React.Fragment>
            ))}
          </div>
          <div className="tw-mt-3 tw-text-lg tw-font-bold">
            Additional Details
          </div>
          <div className="summary-container tw-mt-3 ml-3">
            <div>
              <p>Style</p>
              <p>{product?.sku}</p>
            </div>
            {ADDITIONAL_TABS.map((tab: ITab, index) => (
              <React.Fragment key={index}>
                {" "}
                {getRowContent(tab, index, { isPDP })}
              </React.Fragment>
            ))}
          </div>
          <If condition={styles.showShippingEstimation && !['legacy-touch', 'legacy-touch-dev'].includes(subpath)}>
            <div className="tw-mt-3 tw-text-lg tw-font-bold">Ship Date</div>
            <div className="summary-container tw-mt-3 ml-3">
              {product?.shipping_options?.same_day ? <div className="tw-mt-1">
                <p>Same Day</p>
                <p>{formatSummaryDate(product?.shipping_options?.same_day)}</p>
              </div> : null}
              {product?.shipping_options?.next_day ? <div className="tw-mt-1">
                <p>Next Day</p>
                <p>{formatSummaryDate(product?.shipping_options?.next_day)}</p>
              </div> : null}
              {product?.shipping_options?.rush ? <div className="tw-mt-1">
                <p>Rush</p>
                <p>{formatSummaryDate(product?.shipping_options?.rush)}</p>
              </div> : null}
              {product?.shipping_options?.standard ? <div className="tw-mt-1">
                <p>Standard</p>
                <p>{formatSummaryDate(product?.shipping_options?.standard)}</p>
              </div> : null}
            </div>
            <div className='tw-ml-3 tw-mt-3 tw-text-xs'>* Contact us if you have any questions or need a ring by a specific date sooner than what's displayed.</div>
          </If>

          <If condition={['daniels-jewelers','daniels-jewelers-dev'].includes(subpath)}>
            <div className="klaviyo-form-Tvh5eL" style={{marginTop: "20px", borderBottom : "solid 1px", borderTop : "solid 1px"}}></div>
          </If>

          {!isPDP && !['revolution', 'stonebrook-jewelry', 'foundry', 'daniels-jewelers', 'daniels-jewelers-dev'].includes(subpath) && (<>
            <button id="print-btn" className="tw-mt-8 !tw-bg-brand-color !tw-text-white hover:tw-text-white tw-px-9 tw-py-2 tw-font-medium tw-normal-case secondary-font-family focus:tw-outline-none" onClick={handlePrint}>Print Summary</button>
            <PrintSummary tabs={tabs} image={images?.[0]} product={product} ref={componentRef} />
          </>)}
          {styles.enableMagicNoSearch && !isPDP && (<div className="tw-mt-8 tw-flex">
            <input type='text' name='magic_number' placeholder='Search Magic number' value={value} onChange={e => {setValue(e.target.value);}} className="tw-p-3 tw-border tw-mr-3 !tw-text-base magic-number" />
            <button id="print-btn" className="!tw-bg-brand-color !tw-text-white hover:tw-text-white tw-px-9 tw-py-2 tw-font-medium tw-normal-case tw-secondary-font-family focus:tw-outline-none" onClick={handleSearch}>Search</button>
          </div>)}
        </div>
        {!!images?.length && (
          <div className="tw-flex-1 gallery_container tw-relative tw-hidden lg:tw-block">
            <MyGallery images={images} />
          </div>
        )}
      </div>
    </div>
  );
};

export default Summary;
